import { CompanySearchRow } from '../../services/service-company-search';
import {
  MunicipalityOption,
  municipalityOption,
  registerIdentity,
} from '../app-company-search/registerIdentity';

export type ProviderCompanyData = {
  name?: string;
  address?: string;
  postnumber?: string;
  kommun?: string;
};

export type CompanyAnswer = { companyData: ProviderCompanyData } | { error: string } | null;

type SupplierProfile = { places?: string[]; logo?: unknown };

export type CompanyLookUp = {
  orgNumber: string;
  answer: CompanyAnswer;
  profile: SupplierProfile | null;
};

type CompanyForm = {
  organizationNumber?: string;
  selectedIdentityField?: { value: string };
  address?: string;
  places?: string[];
  logo?: unknown;
};

export type LookUpState<D extends CompanyForm> = {
  data: D;
  formErrors: Record<string, string>;
  showQuickCheck: boolean;
  supplierProfile?: SupplierProfile;
};

type ProviderFields = {
  name?: string;
  address?: string;
  postnumber?: string;
  kommun?: MunicipalityOption;
};

const lines = (...parts: (string | null | undefined)[]) =>
  parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join('\n');

export function registerFields(company: CompanySearchRow, municipalities: MunicipalityOption[]) {
  const postalLine = [company.postalCode, company.city].filter(Boolean).join(' ');
  return {
    ...registerIdentity(company, municipalities),
    address: lines(company.street, postalLine),
    postnumber: company.postalCode ?? '',
  };
}

export function providerFields(
  company: ProviderCompanyData,
  municipalities: MunicipalityOption[],
  formAddress = '',
): ProviderFields {
  const fields: ProviderFields = {};
  if (company.name?.trim()) fields.name = company.name;

  const [street, ...postalLines] = (company.address ?? '').split('\n');
  const address = lines(street, ...postalLines);
  if (address && (street.trim() || !formAddress.trim())) {
    fields.address = address;
    if (company.postnumber) fields.postnumber = company.postnumber;
  }

  const kommun = municipalityOption(company.kommun, municipalities);
  if (kommun) fields.kommun = kommun;
  return fields;
}

// Same reference means the profile filled it in, not the user.
export function withoutProfileFields<D extends CompanyForm>(data: D, profile?: SupplierProfile): D {
  return {
    ...data,
    places: data.places === profile?.places ? [] : data.places,
    logo: data.logo === profile?.logo ? undefined : data.logo,
  };
}

export function mergeCompanyLookUp<D extends CompanyForm>(
  prev: LookUpState<D>,
  { orgNumber, answer, profile }: CompanyLookUp,
  municipalities: MunicipalityOption[],
): LookUpState<D> | null {
  const { data } = prev;
  const stillAsked =
    data.organizationNumber === orgNumber &&
    data.selectedIdentityField?.value === 'organizationNumber';
  if (!stillAsked) return null;

  const provided =
    answer && 'companyData' in answer
      ? providerFields(answer.companyData, municipalities, data.address)
      : {};
  return {
    data: {
      ...(profile ? withoutProfileFields(data, prev.supplierProfile) : data),
      ...provided,
      ...(profile?.places?.length ? { places: profile.places } : {}),
      ...(profile?.logo ? { logo: profile.logo } : {}),
    },
    formErrors: {
      ...prev.formErrors,
      ...(provided.name ? { name: '' } : {}),
      ...(provided.kommun ? { kommun: '' } : {}),
      ...(answer && 'error' in answer ? { organizationNumber: answer.error } : {}),
    },
    showQuickCheck: answer ? 'companyData' in answer : prev.showQuickCheck,
    supplierProfile: profile ?? prev.supplierProfile,
  };
}
